import { useState } from 'react';
import toast from 'react-hot-toast';
import {
  ArrowClockwiseIcon,
  CheckCircleIcon,
  CopySimpleIcon,
  DotsThreeIcon,
  EnvelopeSimpleIcon,
  PlusIcon,
  ProhibitIcon,
  TrashIcon,
} from '@phosphor-icons/react';

import {
  useTeamMembers,
  useTeamInvitations,
  inviteMember,
  removeMember,
  updateMemberRole,
  setMemberStatus,
  revokeInvitation,
  resendInvitation,
  updateInvitationRole,
  type TeamMember,
  type TeamInvitation,
  type TeamRole,
} from '@/api-models/teams';
import { TEAM_ROLE_LABEL } from '@/features/teams/components/teamVisuals';
import { useSpace } from '@/contexts/space-context';
import { useAuth } from '@/contexts/auth-context';
import { Button, IconButton } from '@/ui/button/Button';
import { Badge } from '@/ui/Badge';
import { Input } from '@/ui/Input';
import { Modal } from '@/ui/Modal';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/ui/DropdownMenu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/Select';
import { Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow } from '@/ui/Table';
import { formatTimeAgo } from '@/utils/format';

/**
 * Team → Members settings page.
 *
 * Full-width section (rendered outside the 720px settings column) with its own
 * header — mirrors the "Settings — Members" design. Backed by the real team
 * roster + pending invitations for the active team, with working role changes,
 * removal, invite, resend, and revoke.
 */

const ROLE_DESCRIPTION: Record<TeamRole, string> = {
  owner: 'Everything an admin can do, plus deleting the team.',
  admin: 'Manage members, integrations, and team settings.',
  member: 'Use projects, workspaces, and playbooks in this team.',
};

const ROLE_OPTIONS = (['owner', 'admin', 'member'] as const).map((value) => ({
  value,
  label: TEAM_ROLE_LABEL[value],
  description: ROLE_DESCRIPTION[value],
}));

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts.at(0);
  if (!first) return '?';
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  const last = parts.at(-1) ?? first;
  return (first.charAt(0) + last.charAt(0)).toUpperCase();
}

function lastActiveLabel(iso: string): string {
  const ago = formatTimeAgo(iso);
  return ago === 'just now' ? 'Active now' : ago;
}

export function MembersContent() {
  const { teamId, teams } = useSpace();
  const { me } = useAuth();
  const { members, mutate: mutateMembers, isLoading } = useTeamMembers(teamId);
  const { invitations, mutate: mutateInvitations } = useTeamInvitations(teamId);

  const myRole = teams.find((t) => t.id === teamId)?.role ?? 'member';
  const allowOwner = myRole === 'owner';
  const pending = invitations.filter((i) => i.status === 'pending');

  const [inviteOpen, setInviteOpen] = useState(false);

  async function handleMemberRole(member: TeamMember, role: TeamRole) {
    if (role === member.role) return;
    try {
      await updateMemberRole(teamId, member.user_id, role);
      await mutateMembers();
      toast.success(`${member.name} is now ${TEAM_ROLE_LABEL[role]}`);
    } catch {
      toast.error('Could not update role');
      await mutateMembers();
    }
  }

  async function handleRemove(member: TeamMember) {
    try {
      await removeMember(teamId, member.user_id);
      await mutateMembers();
      toast.success(`Removed ${member.name}`);
    } catch {
      toast.error('Could not remove member');
    }
  }

  async function handleStatus(member: TeamMember, status: 'active' | 'deactivated') {
    try {
      await setMemberStatus(teamId, member.user_id, status);
      await mutateMembers();
      toast.success(status === 'deactivated' ? `Deactivated ${member.name}` : `Reactivated ${member.name}`);
    } catch {
      toast.error('Could not update member');
      await mutateMembers();
    }
  }

  async function handleInviteRole(inv: TeamInvitation, role: TeamRole) {
    if (role === inv.role) return;
    try {
      await updateInvitationRole(teamId, inv.id, role);
      await mutateInvitations();
    } catch {
      toast.error('Could not update role');
      await mutateInvitations();
    }
  }

  async function handleResend(inv: TeamInvitation) {
    try {
      await resendInvitation(teamId, inv.id);
      await mutateInvitations();
      toast.success(`Invitation resent to ${inv.email}`);
    } catch {
      toast.error('Could not resend invitation');
    }
  }

  async function handleRevoke(inv: TeamInvitation) {
    try {
      await revokeInvitation(teamId, inv.id);
      await mutateInvitations();
      toast.success(`Revoked invitation for ${inv.email}`);
    } catch {
      toast.error('Could not revoke invitation');
    }
  }

  return (
    <div className="flex flex-col gap-7">
      {/* Header */}
      <div className="flex items-start justify-between gap-6">
        <div className="flex flex-col">
          <h1 className="page-title">Members</h1>
          <p className="page-subtitle">Invite people, manage their roles, and track pending invitations.</p>
        </div>
        <Button
          type="button"
          variant="primary"
          size={9}
          onClick={() => setInviteOpen(true)}
          iconLeft={<PlusIcon size={16} weight="bold" />}
        >
          Invite people
        </Button>
      </div>

      {/* Members table */}
      <div className="overflow-hidden rounded-xl border border-line bg-surface-1">
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell>Member</TableHeaderCell>
              <TableHeaderCell className="w-52">Role</TableHeaderCell>
              <TableHeaderCell className="w-37.5">Status</TableHeaderCell>
              <TableHeaderCell className="w-42.5">Last active</TableHeaderCell>
              <TableHeaderCell className="w-15" />
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && members.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-fg-tertiary">
                  Loading members…
                </TableCell>
              </TableRow>
            ) : members.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-fg-tertiary">
                  No members found.
                </TableCell>
              </TableRow>
            ) : (
              members.map((m) => {
                const deactivated = m.status === 'deactivated';
                return (
                  <TableRow key={m.id}>
                    <TableCell>
                      <div className={`flex min-w-0 items-center gap-3 ${deactivated ? 'opacity-50' : ''}`}>
                        <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-brand text-2xs font-semibold text-white">
                          {initialsOf(m.name)}
                        </span>
                        <div className="flex min-w-0 flex-col gap-0.5">
                          <span className="truncate text-sm font-semibold text-fg">
                            {m.name}
                            {m.user_id === me.id && <span className="ml-1 font-normal text-fg-tertiary">(you)</span>}
                          </span>
                          <span className="truncate text-xs font-medium text-fg-tertiary">{m.email}</span>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <RoleSelect value={m.role} allowOwner={allowOwner} onChange={(r) => handleMemberRole(m, r)} />
                    </TableCell>
                    <TableCell>
                      <Badge variant={deactivated ? 'neutral' : 'success'} size="md" dot>
                        {deactivated ? 'Deactivated' : 'Active'}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-medium">{lastActiveLabel(m.last_seen_at)}</TableCell>
                    <TableCell>
                      <div className="flex justify-end">
                        <MemberMenu
                          member={m}
                          isSelf={m.user_id === me.id}
                          onRemove={() => handleRemove(m)}
                          onSetStatus={(s) => handleStatus(m, s)}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pending invitations */}
      {pending.length > 0 && (
        <div className="flex flex-col gap-3.5">
          <div className="flex items-center gap-2.25">
            <h2 className="card-heading">Pending invitations</h2>
            <Badge variant="neutral" size="md">
              {pending.length}
            </Badge>
          </div>

          <div className="overflow-hidden rounded-xl border border-line bg-surface-1">
            {pending.map((inv, i) => (
              <div
                key={inv.id}
                className={`flex h-16.5 items-center gap-3.5 px-5 ${i > 0 ? 'border-t border-line-subtle' : ''}`}
              >
                <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-surface-3 text-fg-secondary">
                  <EnvelopeSimpleIcon size={17} />
                </span>
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="truncate text-sm font-semibold text-fg">{inv.email}</span>
                  <span className="truncate text-xs font-medium text-fg-tertiary">
                    Invited {formatTimeAgo(inv.created_at)}
                  </span>
                </div>
                <div className="w-32">
                  <RoleSelect value={inv.role} allowOwner={allowOwner} onChange={(r) => handleInviteRole(inv, r)} />
                </div>
                <Badge variant="warning" size="md" dot>
                  Invited
                </Badge>
                <Button
                  type="button"
                  variant="secondary"
                  size={8}
                  onClick={() => handleResend(inv)}
                  iconLeft={<ArrowClockwiseIcon size={14} />}
                >
                  Resend
                </Button>
                <Button type="button" variant="ghost" size={8} onClick={() => handleRevoke(inv)}>
                  Revoke
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {inviteOpen && (
        <InviteModal
          teamId={teamId}
          allowOwner={allowOwner}
          onClose={() => setInviteOpen(false)}
          onInvited={mutateInvitations}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Building blocks
// ---------------------------------------------------------------------------

function MemberMenu({
  member,
  isSelf,
  onRemove,
  onSetStatus,
}: {
  member: TeamMember;
  isSelf: boolean;
  onRemove: () => void;
  onSetStatus: (status: 'active' | 'deactivated') => void;
}) {
  const deactivated = member.status === 'deactivated';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <IconButton size={8} aria-label="Member actions">
          <DotsThreeIcon size={18} weight="bold" />
        </IconButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          onSelect={() => {
            navigator.clipboard?.writeText(member.email);
            toast.success('Email copied');
          }}
        >
          <CopySimpleIcon size={15} />
          Copy email
        </DropdownMenuItem>
        {!isSelf &&
          (deactivated ? (
            <DropdownMenuItem onSelect={() => onSetStatus('active')}>
              <CheckCircleIcon size={15} />
              Reactivate
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem onSelect={() => onSetStatus('deactivated')}>
              <ProhibitIcon size={15} />
              Deactivate
            </DropdownMenuItem>
          ))}
        {!isSelf && (
          <DropdownMenuItem destructive onSelect={onRemove}>
            <TrashIcon size={15} />
            Remove from team
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function RoleSelect({
  value,
  onChange,
  allowOwner,
}: {
  value: TeamRole;
  onChange: (role: TeamRole) => void;
  allowOwner: boolean;
}) {
  return (
    <Select value={value} onValueChange={(v) => onChange(v as TeamRole)}>
      <SelectTrigger aria-label="Role">
        <SelectValue>{TEAM_ROLE_LABEL[value]}</SelectValue>
      </SelectTrigger>
      <SelectContent align="start">
        {ROLE_OPTIONS.map((role) => (
          <SelectItem
            key={role.value}
            value={role.value}
            description={role.description}
            disabled={role.value === 'owner' && !allowOwner}
          >
            {role.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function InviteModal({
  teamId,
  allowOwner,
  onClose,
  onInvited,
}: {
  teamId: string;
  allowOwner: boolean;
  onClose: () => void;
  onInvited: () => void;
}) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<TeamRole>('member');
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim() || busy) return;
    setBusy(true);
    try {
      await inviteMember(teamId, { email: email.trim(), role });
      onInvited();
      toast.success(`Invitation sent to ${email.trim()}`);
      onClose();
    } catch {
      toast.error('Could not send invitation');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal isOpen onClose={onClose} title="Invite people" description="Send an invitation to join this team.">
      <form onSubmit={submit} className="flex flex-col gap-4 px-6 pb-6">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="invite-email" className="text-xs font-medium text-fg">
            Email
          </label>
          <Input
            id="invite-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="colleague@company.com"
            autoFocus
          />
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-fg">Role</span>
          <div className="w-40">
            <RoleSelect value={role} allowOwner={allowOwner} onChange={setRole} />
          </div>
        </div>
        <div className="mt-1 flex justify-end gap-2">
          <Button type="button" variant="secondary" size={9} onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" size={9} disabled={!email.trim()} isLoading={busy}>
            Send invitation
          </Button>
        </div>
      </form>
    </Modal>
  );
}
